import { useState, useEffect, useRef } from "react";
import { useNavigate } from "react-router-dom";
import axios from "axios";
import Box from "@mui/material/Box";
import Container from "@mui/material/Container";
import Typography from "@mui/material/Typography";
import Button from "@mui/material/Button";
import Alert from "@mui/material/Alert";
import Stepper from "@mui/material/Stepper";
import Step from "@mui/material/Step";
import StepLabel from "@mui/material/StepLabel";
import MobileStepper from "@mui/material/MobileStepper";
import { Modal } from "@mui/material";
import Loader from "../animations/Loader";
import Tick from "../animations/Tick";
import ReferenceExampleModal from "./ReferenceExampleModal";
import { handleUK, handleEmployment, handleMedical, handleConvictions, handleSmoke } from "./nspHandlers";
import StepPersonal from "./steps/StepPersonal";
import StepRightToWork from "./steps/StepRightToWork";
import StepContactAndId from "./steps/StepContactAndId";
import StepNextOfKinAndBank from "./steps/StepNextOfKinAndBank";
import StepReferencesAndWork from "./steps/StepReferencesAndWork";
import StepMedicalAndConvictions from "./steps/StepMedicalAndConvictions";
import StepPolicies from "./steps/StepPolicies";
import StepSignAndSubmit from "./steps/StepSignAndSubmit";
import Layout from "../Layout";
import "./styles/newstart.scss";

const STEP_TITLES = [
  "Personal details",
  "Right to work",
  "Contact & ID",
  "Next of kin & Bank",
  "References & work",
  "Medical & convictions",
  "Policies",
  "Sign & submit",
];

const initialForm = {
  title: "",
  firstName: "",
  middleName: "",
  surname: "",
  address: "",
  address2: "",
  city: "",
  postCode: "",
  country: "",
  homePhone: "",
  mobilePhone: "",
  DOB: "",
  NIN: "",
  EU: null,
  shareCode: "",
  shareExpire: "",
  visaType: "",
  visaReason: null,
  candidateMail: "",
  confirmMail: "",
  poa: "",
  poId: "",
  NOKname: "",
  NOKsurname: "",
  NOKPhone: "",
  bankName: "",
  shortCode: "",
  bankAcc: "",
  smoke: "",
  referenceOnce: "",
  refLastOne: "",
  refCompanyOne: "",
  refEmail: "",
  ref1type: "",
  referenceTwo: "",
  refLastTwo: "",
  refCompanyTwo: "",
  refEmailTwo: "",
  ref2type: "",
  agreeToReferenceCheck: false,
  availability: "",
  employementStatus: "",
  medical: "",
  medicalAbility: "",
  medicalNotes: "",
  conviction: "",
  convictionNotes: "",
  alcohol: false,
  safety: false,
  enviromental: false,
  deductions: false,
};

export default function NspWizard() {
  const navigate = useNavigate();
  const url = process.env.REACT_APP_HR_API_URL;
  const [form, setForm] = useState(initialForm);
  const [currentStep, setCurrentStep] = useState(0);
  const [showBox, setShowBox] = useState(false);
  const [medBox, setMedBox] = useState(false);
  const [convBox, setConvBox] = useState(false);
  const [linkStatus, setLinkStatus] = useState("validating");
  const [linkErrorMessage, setLinkErrorMessage] = useState("");
  const [interviewId, setInterviewId] = useState("");
  const [employeeDetailsId, setEmployeeDetailsId] = useState(null);
  const [site, setSite] = useState("");
  const [siteAddress, setSiteAddress] = useState("");
  const [contractedHours, setContractedHours] = useState("");
  const [position, setPosition] = useState("");
  const [start, setStart] = useState("");
  const [ip, setIp] = useState("");
  const [openReferenceLetter, setOpenReferenceLetter] = useState(false);
  const [showTick, setShowTick] = useState(false);
  const [showLoader, setShowLoader] = useState(false);
  const [message, setMessage] = useState("");
  const [severity, setSeverity] = useState("");
  const [errorAlert, setErrorAlert] = useState(false);
  const signatureRef = useRef(null);
  const alertRef = useRef(null);

  const update = (key, value) => {
    setForm((prev) => ({ ...prev, [key]: value }));
  };

  useEffect(() => {
    const params = new URLSearchParams(window.location.search);
    const id = params.get("id") || params.get("employeeDetailsId");
    const token = params.get("token");
    if (token) sessionStorage.setItem("onboardingToken", token);
    if (params.get("id")) setInterviewId(params.get("id"));
    else if (id) setEmployeeDetailsId(id);

    if (!id) {
      setLinkStatus("invalid");
      setLinkErrorMessage("Invalid or missing link. Please use the link from your email or reach out to our HR Team.");
      return;
    }

    const storedToken = sessionStorage.getItem("onboardingToken");
    if (!token && !storedToken) {
      setLinkStatus("invalid");
      setLinkErrorMessage("Invalid or missing link. Please use the link from your email or reach out to our HR Team.");
      return;
    }

    setLinkStatus("validating");
    setLinkErrorMessage("");
    const headers = storedToken ? { "x-onboarding-token": storedToken } : {};
    axios
      .get(`${url}/onBoard/start/${id}`, { headers })
      .then((res) => {
        if (res.data.nspCompleted === true) {
          navigate("/nsp/confirm");
        } else {
          setSite(res.data.Site || "");
          setSiteAddress(res.data.address || "");
          setContractedHours(res.data.contract || "");
          setPosition(res.data.position || "");
          setStart(res.data.start || "");
          if (res.data.trial) {
            const t = res.data.trial;
            const ukCitizen =
              t.ukCitizen === true || t.ukCitizen === false ? t.ukCitizen : null;
            if (ukCitizen === false) setShowBox(true);
            else if (ukCitizen === true) setShowBox(false);
            setForm((prev) => ({
              ...prev,
              firstName: t.firstName || prev.firstName,
              middleName: t.middleName || prev.middleName,
              surname: t.lastName || prev.surname,
              DOB: t.dob || prev.DOB,
              NOKname: t.nokFirstName || prev.NOKname,
              NOKsurname: t.nokLastName || prev.NOKsurname,
              NOKPhone: t.nokPhone || prev.NOKPhone,
              trialPoiHeld: !!t.poiHeld,
              EU: ukCitizen !== null ? ukCitizen : prev.EU,
              shareCode: ukCitizen === false ? (t.shareCode || prev.shareCode) : prev.shareCode,
              shareExpire: ukCitizen === false ? (t.shareExpire || prev.shareExpire) : prev.shareExpire,
              visaType: ukCitizen === false ? (t.visaType || prev.visaType) : prev.visaType,
              visaReason: ukCitizen === false ? (t.visaReason ?? prev.visaReason) : prev.visaReason,
            }));
          }
          setLinkStatus("valid");
        }
      })
      .catch((err) => {
        const status = err.response?.status;
        setLinkStatus("invalid");
        if (status === 401) {
          setLinkErrorMessage("Invalid or expired link. Please use the link from your email or reach out to our HR Team.");
        } else if (status === 400 || status === 404) {
          setLinkErrorMessage("Can not find any details for this individual.");
        } else {
          setLinkErrorMessage("Something went wrong. Please try again.");
        }
      });

    axios
      .get("https://api.ipify.org/?format=json")
      .then((res) => setIp(res.data.ip || ""))
      .catch(() => {});
  }, [url, navigate]);

  const showAlert = (msg, sev = "warning") => {
    setMessage(msg);
    setSeverity(sev);
    setErrorAlert(true);
    setTimeout(() => {
      if (alertRef.current) {
        alertRef.current.scrollIntoView({ behavior: "smooth", block: "nearest" });
      }
    }, 100);
    setTimeout(() => {
      setErrorAlert(false);
      setMessage("");
      setSeverity("");
    }, 5000);
  };

  function validateStep(step) {
    const regex = /^[a-zA-Z0-9.!#$%&'*+/=?^_`{|}~-]+@[a-zA-Z0-9-]+(?:\.[a-zA-Z0-9-]+)*$/;
    const f = form;

    if (step === 0) {
      const missing = [];
      if (!f.title || f.title === "--Please Select One--") missing.push("Title");
      if (!f.firstName?.trim()) missing.push("First name");
      if (!f.surname?.trim()) missing.push("Last name");
      if (!f.address?.trim()) missing.push("Address");
      if (!f.city?.trim()) missing.push("City");
      if (!f.postCode?.trim()) missing.push("Post code");
      if (!f.mobilePhone?.trim()) missing.push("Mobile number");
      if (!f.DOB) missing.push("Date of birth");
      if (!f.NIN?.trim()) missing.push("National Insurance number");
      if (missing.length > 0) {
        showAlert("Please complete: " + missing.join(", "));
        return false;
      }
      return true;
    }

    if (step === 1) {
      if (f.EU === null) {
        showAlert("Please select whether you are a UK citizen.");
        return false;
      }
      if (f.EU === false) {
        const visaMissing = [];
        if (!f.shareCode?.trim()) visaMissing.push("Share code");
        if (!f.shareExpire) visaMissing.push("Visa expiry date");
        if (!f.visaType?.trim()) visaMissing.push("Visa type");
        if (f.visaType === "Other" && (!f.visaReason || f.visaReason == null)) visaMissing.push("Details for visa type ‘Other’");
        if (visaMissing.length > 0) {
          showAlert("Please complete your visa details: " + visaMissing.join(", "));
          return false;
        }
      }
      return true;
    }

    if (step === 2) {
      if (f.candidateMail !== f.confirmMail) {
        showAlert("Your email and confirmation don’t match. Please enter the same email in both fields.");
        return false;
      }
      if (!f.candidateMail?.toLowerCase().match(regex)) {
        showAlert("Please enter a valid email address.");
        return false;
      }
      const docMissing = [];
      if (!f.poa) docMissing.push("Proof of address");
      if (!f.poId && !f.trialPoiHeld) docMissing.push("Proof of ID");
      if (docMissing.length > 0) {
        showAlert("Please upload: " + docMissing.join(" and "));
        return false;
      }
      return true;
    }

    if (step === 3) {
      const nokMissing = [];
      if (!f.NOKname?.trim()) nokMissing.push("Next of Kin first name");
      if (!f.NOKsurname?.trim()) nokMissing.push("Next of Kin last name");
      if (!f.NOKPhone?.trim()) nokMissing.push("Next of Kin phone number");
      if (nokMissing.length > 0) {
        showAlert("Please complete Next of Kin: " + nokMissing.join(", "));
        return false;
      }
      const sortcode = (f.shortCode || "").replaceAll("-", "").replace(/\s/g, "");
      const bankMissing = [];
      if (!f.bankName?.trim()) bankMissing.push("Bank name");
      if (!sortcode) bankMissing.push("Sort code");
      if (!f.bankAcc?.trim()) bankMissing.push("Account number");
      if (bankMissing.length > 0) {
        showAlert("Please complete bank details: " + bankMissing.join(", "));
        return false;
      }
      if (sortcode.length !== 6) {
        showAlert("Sort code must be exactly 6 digits. Please double-check your sort code.");
        return false;
      }
      if (f.bankAcc.length !== 8) {
        showAlert("Account number must be exactly 8 digits. Please double-check your account number.");
        return false;
      }
      return true;
    }

    if (step === 4) {
      if (!f.smoke) {
        showAlert("Please read and confirm the smoke/vape policy.");
        return false;
      }
      const refMissing = [];
      if (!f.referenceOnce?.trim()) refMissing.push("Reference 1 first name");
      if (!f.refLastOne?.trim()) refMissing.push("Reference 1 last name");
      if (!f.refCompanyOne?.trim()) refMissing.push("Reference 1 company");
      if (!f.refEmail?.trim()) refMissing.push("Reference 1 email");
      if (!f.ref1type || f.ref1type === "--Please Select One--") refMissing.push("Reference 1 type");
      if (!f.referenceTwo?.trim()) refMissing.push("Reference 2 first name");
      if (!f.refLastTwo?.trim()) refMissing.push("Reference 2 last name");
      if (!f.refCompanyTwo?.trim()) refMissing.push("Reference 2 company");
      if (!f.refEmailTwo?.trim()) refMissing.push("Reference 2 email");
      if (!f.ref2type || f.ref2type === "--Please Select One--") refMissing.push("Reference 2 type");
      if (refMissing.length > 0) {
        showAlert("Please complete references: " + refMissing.join(", "));
        return false;
      }
      if (!f.refEmail?.toLowerCase().match(regex) || !f.refEmailTwo?.toLowerCase().match(regex)) {
        showAlert("Please enter valid email addresses for both references.");
        return false;
      }
      if (f.refEmail?.toLowerCase() === f.refEmailTwo?.toLowerCase()) {
        showAlert("Reference 1 and Reference 2 emails must be different.");
        return false;
      }
      if (f.ref1type === "Personal" && f.ref2type === "Personal") {
        showAlert("Both references cannot be personal references. Please add at least one professional/employer reference.");
        return false;
      }
      if (!f.agreeToReferenceCheck) {
        showAlert("Please tick to authorise us to contact your referees.");
        return false;
      }
      if (!f.employementStatus) {
        showAlert("Please mark your current employment status.");
        return false;
      }
      if (!f.availability?.trim()) {
        showAlert("Please fill in your availability.");
        return false;
      }
      return true;
    }

    if (step === 5) {
      if (!f.medical) {
        showAlert("Please indicate whether you have any medical or mental health conditions we should know about.");
        return false;
      }
      if (!f.conviction) {
        showAlert("Please indicate whether you have any unspent criminal convictions.");
        return false;
      }
      const med = f.medical !== "no";
      const convicts = f.conviction === "yes";
      if (med && (!f.medicalNotes?.trim() || !f.medicalAbility?.trim())) {
        showAlert("Please specify your medical condition and how we can support you (ability and description).");
        return false;
      }
      if (convicts && !f.convictionNotes?.trim()) {
        showAlert("Please provide details of your unspent convictions.");
        return false;
      }
      return true;
    }

    if (step === 6) {
      const policyMissing = [];
      if (!f.alcohol) policyMissing.push("Alcohol policy");
      if (!f.enviromental) policyMissing.push("Environmental policy");
      if (!f.safety) policyMissing.push("Safety policy");
      if (!f.deductions) policyMissing.push("Deductions policy");
      if (policyMissing.length > 0) {
        showAlert("Please read and agree to all four policies: " + policyMissing.join(", "));
        return false;
      }
      return true;
    }

    return true;
  }

  const handleNext = () => {
    if (validateStep(currentStep)) setCurrentStep((s) => Math.min(s + 1, STEP_TITLES.length - 1));
  };

  const handleBack = () => {
    setCurrentStep((s) => Math.max(s - 1, 0));
  };

  const handleSubmit = async () => {
    const f = form;
    if (!validateStep(6)) return;
    if (!signatureRef?.current || signatureRef.current.isEmpty()) {
      showAlert("Please sign in the signature box before submitting.");
      return;
    }
    if (!f.availability?.trim()) {
      showAlert("Please fill in your availability in step 5 (References & work).");
      return;
    }

    setShowLoader(true);
    const signatureData = signatureRef.current.getTrimmedCanvas().toDataURL("image/png");
    const sortcode = (f.shortCode || "").replaceAll("-", "");
    const med = f.medical === "no" ? false : true;
    const convicts = f.conviction === "no" ? false : true;
    const smk = f.smoke === "no" ? false : true;
    let visaData = f.visaType || "";
    if (f.visaReason) visaData += ` - ${f.visaReason}`;

    const empObj = {
      interviewId,
      employeeDetailsId,
      ipAddress: ip,
      title: f.title,
      firstName: f.firstName,
      middleName: f.middleName,
      lastName: f.surname,
      addressLine1: f.address,
      addressLine2: f.address2,
      city: f.city,
      country: f.country,
      postcode: f.postCode,
      homePhone: (f.homePhone || "").replace(/\s/g, ""),
      mobilePhone: (f.mobilePhone || "").replace(/\s/g, ""),
      dob: f.DOB,
      nationalInsuranceNumber: f.NIN,
      email: f.candidateMail,
      startDate: start,
      mainCompanyId: site,
      roleId: position,
      ukCitizen: f.EU,
      shareCode: f.shareCode,
      shareExpire: f.shareExpire,
      visaType: visaData,
      poa: f.poa,
      poi: f.poId,
      availability: f.availability,
      references: [
        {
          name: `${f.referenceOnce} ${f.refLastOne}`,
          email: f.refEmail?.toLowerCase(),
          company: f.refCompanyOne,
          type: f.ref1type,
        },
        {
          name: `${f.referenceTwo} ${f.refLastTwo}`,
          email: f.refEmailTwo?.toLowerCase(),
          company: f.refCompanyTwo,
          type: f.ref2type,
        },
      ],
      agreeToReferenceCheck: f.agreeToReferenceCheck,
      contractedHours,
      bankingDetails: {
        bankName: f.bankName,
        sortCode: sortcode,
        accountNumber: f.bankAcc,
      },
      nextOfKin: {
        firstName: f.NOKname,
        lastName: f.NOKsurname,
        phoneNumber: f.NOKPhone,
      },
      additionalDetails: {
        smoker: smk,
        employementStatus: f.employementStatus,
        medicalAssistance: med,
        ability: f.medicalAbility,
        medicalDescription: f.medicalNotes,
        criminalConvictions: convicts,
        convictionsDescription: f.convictionNotes,
      },
      policy: {
        alcohol: f.alcohol,
        safety: f.safety,
        enviromental: f.enviromental,
        signature: signatureData,
      },
    };

    const headers = sessionStorage.getItem("onboardingToken")
      ? { "x-onboarding-token": sessionStorage.getItem("onboardingToken") }
      : {};
    try {
      const res = await axios.post(`${url}/employee/add`, empObj, { headers });
      if (res.status === 200) {
        setShowLoader(false);
        setShowTick(true);
        setTimeout(() => navigate("/nsp/confirm"), 3500);
      } else if (res.status === 403 && res.data?.nspCompleted) {
        setShowLoader(false);
        showAlert("This form has already been completed.", "info");
        setTimeout(() => navigate("/nsp/confirm"), 2000);
      } else if (res.status === 500) {
        setShowLoader(false);
        showAlert("Something went wrong, please try again");
      }
    } catch (err) {
      setShowLoader(false);
      if (err?.response?.status === 403 && err?.response?.data?.nspCompleted) {
        showAlert("This form has already been completed.", "info");
        setTimeout(() => navigate("/nsp/confirm"), 2000);
      } else {
        showAlert("Form not submitted, please try again", "error");
      }
    }
  };

  const handleUKChange = (uk) => {
    handleUK(uk, (v) => update("EU", v), setShowBox);
  };

  const handleSmokeChange = (conf) => {
    handleSmoke(conf, form.smoke, update.bind(null, "smoke"));
  };

  const handleEmploymentChange = (status) => {
    handleEmployment(
      status,
      form.employementStatus,
      (v) => update("employementStatus", v),
      setMessage,
      setErrorAlert,
      setSeverity
    );
  };

  const handleMedicalChange = (med) => {
    handleMedical(
      med,
      form.medical,
      (v) => update("medical", v),
      setMedBox,
      setMessage,
      setErrorAlert,
      setSeverity
    );
  };

  const handleConvictionsChange = (conv) => {
    handleConvictions(
      conv,
      form.conviction,
      (v) => update("conviction", v),
      setConvBox
    );
  };

  const onAlert = { setMessage, setErrorAlert, setSeverity };

  const stepProps = {
    data: { ...form, showBox },
    update,
    onAlert,
  };

  if (linkStatus === "validating") {
    return (
      <Layout>
        <Container maxWidth="sm" sx={{ py: 4, textAlign: "center" }}>
          <Typography variant="h2" sx={{ mb: 2 }}>
            Checking your link…
          </Typography>
          <Loader />
        </Container>
      </Layout>
    );
  }

  if (linkStatus === "invalid") {
    return (
      <Layout>
        <Container maxWidth="sm" sx={{ py: { xs: 3, sm: 5 } }}>
          <Alert severity="error">{linkErrorMessage}</Alert>
        </Container>
      </Layout>
    );
  }

  return (
    <Layout>
      <Container maxWidth="sm" sx={{ py: { xs: 2, sm: 4 }, color: "text.primary" }}>
        <Typography variant="h1" sx={{ fontSize: "var(--h1)", textAlign: "center", mb: 2 }}>
          New Start Form
        </Typography>

        <Box sx={{ display: { xs: "none", sm: "block" } }}>
          <Stepper activeStep={currentStep} sx={{ mb: 3 }}>
            {STEP_TITLES.map((label) => (
              <Step key={label}>
                <StepLabel>{label}</StepLabel>
              </Step>
            ))}
          </Stepper>
        </Box>

        <Box sx={{ display: { xs: "block", sm: "none" }, mb: 2 }}>
          <MobileStepper
            variant="progress"
            steps={STEP_TITLES.length}
            position="static"
            activeStep={currentStep}
            LinearProgressProps={{ sx: { width: "100%", height: 8, borderRadius: 4 } }}
            backButton={<Box sx={{ width: 0 }} />}
            nextButton={<Box sx={{ width: 0 }} />}
            sx={{ p: 0, backgroundColor: "transparent" }}
          />
        </Box>

        <Typography sx={{ fontSize: "1rem", color: "text.primary", mb: 2, textAlign: "center" }}>
          Step {currentStep + 1} of {STEP_TITLES.length} — {STEP_TITLES[currentStep]}
        </Typography>

        {currentStep === 0 && <StepPersonal {...stepProps} />}
        {currentStep === 1 && (
          <StepRightToWork
            {...stepProps}
            showBox={showBox}
            onUKChange={handleUKChange}
          />
        )}
        {currentStep === 2 && <StepContactAndId {...stepProps} />}
        {currentStep === 3 && <StepNextOfKinAndBank {...stepProps} />}
        {currentStep === 4 && (
          <StepReferencesAndWork
            {...stepProps}
            onSmokeChange={handleSmokeChange}
            onEmploymentChange={handleEmploymentChange}
            onOpenReferenceLetter={setOpenReferenceLetter}
          />
        )}
        {currentStep === 5 && (
          <StepMedicalAndConvictions
            {...stepProps}
            medBox={medBox}
            convBox={convBox}
            onMedicalChange={handleMedicalChange}
            onConvictionsChange={handleConvictionsChange}
          />
        )}
        {currentStep === 6 && <StepPolicies {...stepProps} />}
        {currentStep === 7 && <StepSignAndSubmit signatureRef={signatureRef} />}

        <Box
          sx={{
            display: "flex",
            justifyContent: "space-between",
            alignItems: "center",
            gap: 2,
            mt: 3,
            "& .MuiButton-root": { flex: { xs: "1 1 auto", sm: "0 0 auto" } },
          }}
        >
          {currentStep > 0 ? (
            <Button variant="outlined" onClick={handleBack}>
              Back
            </Button>
          ) : (
            <span />
          )}
          {currentStep < STEP_TITLES.length - 1 ? (
            <Button variant="contained" onClick={handleNext}>
              Next
            </Button>
          ) : (
            <Button
              variant="contained"
              color="success"
              id="nsp_btn"
              onClick={handleSubmit}
            >
              Submit
            </Button>
          )}
        </Box>

        {errorAlert && (
          <Alert ref={alertRef} severity={severity} sx={{ mt: 2 }}>
            {message}
          </Alert>
        )}
        {showLoader && <Loader />}
        {showTick && <Tick />}
      </Container>
      <Modal open={openReferenceLetter} onClose={() => setOpenReferenceLetter(false)}>
        <ReferenceExampleModal
          setOpenReferenceLetter={setOpenReferenceLetter}
          site={site}
          siteAddress={siteAddress}
        />
      </Modal>
    </Layout>
  );
}
